import React, { useState, useEffect } from 'react';
import { 
  HelpCircle, 
  ChevronDown, 
  ChevronUp, 
  Search,
  Shield,
  Network,
  Globe,
  Code,
  FileCode,
  Lightbulb,
  MessageCircle,
  Phone,
  Mail
} from 'lucide-react';
import Header from "../Header";
import Footer from "../Footer";
import api from '../api/Api';

export default function FAQPage({ isDarkMode, setIsDarkMode }) {
  const [expandedFaq, setExpandedFaq] = useState(null);
  const [selectedService, setSelectedService] = useState('all');
  const [searchQuery, setSearchQuery] = useState('');
  const [faqData, setFaqData] = useState({});
  const [loading, setLoading] = useState(true);

  // Mapa de ícones para serviços
  const serviceIconMap = {
    'CCTV': Shield,
    'Cabeamento Estruturado': Network,
    'Criação de Websites': Globe,
    'Software Personalizado': Code,
    'Software Certificado pela AGT': FileCode,
    'Consultoria Informática': Lightbulb
  };

  const getServiceIcon = (serviceName) => {
    return serviceIconMap[serviceName] || HelpCircle;
  };

  useEffect(() => {
    document.title = "FAQ - Perguntas Frequentes";
    fetchFaq();
  }, []);

  const fetchFaq = async () => {
    setLoading(true);
    try {
      const response = await api.ListFaq();
      
    
      const dados = Array.isArray(response) ? response : [];

      // Agrupar FAQs por serviço
      const grouped = dados.reduce((acc, item) => {
        const service = item.titulo || 'Outros';
        if (!acc[service]) {
          acc[service] = [];
        }
        acc[service].push({
          question: item.pergunta,
          answer: item.resposta
        });
        return acc;
      }, {});

      setFaqData(grouped);
    } catch (error) {
      console.error('Erro ao carregar FAQs:', error);
      // FAQs de fallback em caso de erro
      setFaqData({
        'CCTV': [
          {
            question: "O que é um sistema de CCTV?",
            answer: "CCTV é um sistema de videovigilância utilizado para monitoramento e segurança de ambientes residenciais e empresariais."
          }
        ]
      });
    } finally {
      setLoading(false);
    }
  };

  // Obter lista de serviços
  const services = Object.keys(faqData);

  // Filtrar FAQs
  const getFilteredFaqs = () => {
    let filtered = {};

    if (selectedService === 'all') {
      filtered = faqData;
    } else {
      filtered = { [selectedService]: faqData[selectedService] };
    }

    // Aplicar busca
    if (searchQuery.trim()) {
      const query = searchQuery.toLowerCase();
      const searchFiltered = {};

      Object.keys(filtered).forEach(service => {
        const matchingFaqs = filtered[service].filter(faq => 
          faq.question.toLowerCase().includes(query) || 
          faq.answer.toLowerCase().includes(query)
        );
        
        if (matchingFaqs.length > 0) {
          searchFiltered[service] = matchingFaqs;
        }
      });

      return searchFiltered;
    }

    return filtered;
  };

  const filteredFaqs = getFilteredFaqs();
  const totalFaqs = Object.values(faqData).reduce((sum, faqs) => sum + faqs.length, 0);

  return (
    <div className={`min-h-screen transition-colors duration-300 ${
      isDarkMode ? 'bg-gray-900 text-white' : 'bg-white text-gray-900'
    }`}>
      {/* Animated Background Particles */}
      <div className="fixed inset-0 overflow-hidden pointer-events-none">
        <div className={`absolute top-0 right-0 w-[800px] h-[800px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-blue-500/10' : 'bg-blue-400/20'
        }`}></div>
        <div className={`absolute bottom-0 left-0 w-[600px] h-[600px] rounded-full blur-[120px] animate-pulse ${
          isDarkMode ? 'bg-purple-500/10' : 'bg-purple-500/20'
        }`} style={{animationDelay: '1s'}}></div>
      </div>

      <Header isDarkMode={isDarkMode} setIsDarkMode={setIsDarkMode} />

      {/* Hero Section */}
      <section className={`relative pt-32 pb-20 px-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-purple-50'
      }`}>
        <div className="max-w-4xl mx-auto relative z-10 text-center">
          <div className="inline-flex items-center space-x-2 px-4 py-2 bg-blue-500/10 border border-blue-500/30 rounded-full mb-6">
            <HelpCircle size={20} className="text-blue-600" />
            <span className={`text-sm font-semibold ${
              isDarkMode ? 'text-cyan-400' : 'text-blue-600'
            }`}>
              Centro de Ajuda
            </span>
          </div>

          <h1 className={`text-4xl md:text-6xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Perguntas <span className="bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">Frequentes</span>
          </h1>
          
          <p className={`text-xl mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Encontre respostas rápidas para as dúvidas mais comuns sobre nossos serviços
          </p>

          {/* Search Bar */}
          <div className="relative max-w-2xl mx-auto mb-8">
            <Search className={`absolute left-4 top-1/2 transform -translate-y-1/2 ${
              isDarkMode ? 'text-gray-500' : 'text-gray-400'
            }`} size={20} />
            <input
              type="text"
              placeholder="Pesquisar perguntas..."
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className={`w-full pl-12 pr-4 py-4 rounded-full text-lg outline-none transition-all ${
                isDarkMode
                  ? 'bg-gray-700 border-2 border-gray-600 text-white placeholder-gray-400 focus:border-cyan-500'
                  : 'bg-white border-2 border-gray-200 text-gray-900 placeholder-gray-500 focus:border-blue-500 shadow-lg'
              }`}
            />
          </div>

          {/* Stats */}
          <div className="flex flex-wrap justify-center gap-8">
            <div className="text-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-blue-600 to-purple-600 bg-clip-text text-transparent">
                {services.length}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Serviços
              </div>
            </div>
            <div className="text-center">
              <div className="text-3xl font-bold bg-gradient-to-r from-purple-600 to-pink-600 bg-clip-text text-transparent">
                {totalFaqs}
              </div>
              <div className={`text-sm ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
                Perguntas
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* Service Filter */}
      <section className={`border-b ${
        isDarkMode ? 'bg-gray-800/50 border-white/10' : 'bg-white border-gray-200'
      }`}>
        <div className="max-w-7xl mx-auto px-6 py-6">
          <div className="flex flex-wrap gap-3 justify-center">
            <button
              onClick={() => setSelectedService('all')}
              className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all ${
                selectedService === 'all'
                  ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                  : isDarkMode
                  ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                  : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
              }`}
            >
              <HelpCircle size={18} />
              <span>Todos</span>
            </button>

            {services.map((service) => {
              const Icon = getServiceIcon(service);
              return (
                <button
                  key={service}
                  onClick={() => setSelectedService(service)}
                  className={`flex items-center space-x-2 px-6 py-3 rounded-full font-medium transition-all ${
                    selectedService === service
                      ? 'bg-gradient-to-r from-blue-600 to-purple-600 text-white shadow-lg scale-105'
                      : isDarkMode
                      ? 'bg-gray-700 text-gray-300 hover:bg-gray-600'
                      : 'bg-gray-100 text-gray-700 hover:bg-gray-200'
                  }`}
                >
                  <Icon size={18} />
                  <span>{service}</span>
                </button>
              );
            })}
          </div>
        </div>
      </section>

      {/* FAQ Content */}
      <section className="py-16 px-6 relative z-10">
        <div className="max-w-4xl mx-auto">
          {loading ? (
            <div className="text-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600 mx-auto mb-4"></div>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Carregando perguntas frequentes...
              </p>
            </div>
          ) : Object.keys(filteredFaqs).length > 0 ? (
            Object.keys(filteredFaqs).map((service, serviceIndex) => {
              const Icon = getServiceIcon(service);
              return (
                <div key={service} className="mb-12 last:mb-0">
                  {/* Service Header */}
                  <div className="flex items-center space-x-3 mb-6">
                    <div className="p-3 bg-gradient-to-r from-blue-600 to-purple-600 rounded-lg">
                      <Icon size={24} className="text-white" />
                    </div>
                    <h2 className={`text-2xl md:text-3xl font-bold ${
                      isDarkMode ? 'text-white' : 'text-gray-900'
                    }`}>
                      {service}
                    </h2>
                    <span className={`px-3 py-1 rounded-full text-sm font-semibold ${
                      isDarkMode ? 'bg-gray-700 text-gray-300' : 'bg-gray-200 text-gray-700'
                    }`}>
                      {filteredFaqs[service].length} {filteredFaqs[service].length === 1 ? 'pergunta' : 'perguntas'}
                    </span>
                  </div>

                  {/* FAQs */}
                  <div className="space-y-4">
                    {filteredFaqs[service].map((faq, faqIndex) => {
                      const faqId = `${serviceIndex}-${faqIndex}`;
                      const isExpanded = expandedFaq === faqId;

                      return (
                        <div
                          key={faqId}
                          className={`rounded-xl overflow-hidden transition-all ${
                            isDarkMode
                              ? 'bg-gray-800 border border-white/10'
                              : 'bg-white border border-gray-200 shadow-lg'
                          }`}
                        >
                          <button
                            onClick={() => setExpandedFaq(isExpanded ? null : faqId)}
                            className={`w-full p-6 text-left flex items-start justify-between transition-colors ${
                              isDarkMode ? 'hover:bg-gray-700' : 'hover:bg-gray-50'
                            }`}
                          >
                            <div className="flex items-start space-x-3 flex-1">
                              <MessageCircle 
                                size={20} 
                                className={`flex-shrink-0 mt-1 ${
                                  isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                                }`} 
                              />
                              <span className={`font-semibold ${
                                isDarkMode ? 'text-white' : 'text-gray-900'
                              }`}>
                                {faq.question}
                              </span>
                            </div>
                            {isExpanded ? (
                              <ChevronUp className={`flex-shrink-0 ${
                                isDarkMode ? 'text-cyan-400' : 'text-blue-600'
                              }`} size={20} />
                            ) : (
                              <ChevronDown className={`flex-shrink-0 ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`} size={20} />
                            )}
                          </button>
                          
                          {isExpanded && (
                            <div className={`px-6 pb-6 border-t ${
                              isDarkMode ? 'border-white/10 bg-gray-900/50' : 'border-gray-200 bg-gray-50'
                            }`}>
                              <p className={`pt-4 leading-relaxed ${
                                isDarkMode ? 'text-gray-400' : 'text-gray-600'
                              }`}>
                                {faq.answer}
                              </p>
                            </div>
                          )}
                        </div>
                      );
                    })}
                  </div>
                </div>
              );
            })
          ) : (
            <div className="text-center py-12">
              <HelpCircle size={64} className={`mx-auto mb-4 ${
                isDarkMode ? 'text-gray-600' : 'text-gray-400'
              }`} />
              <h3 className={`text-xl font-bold mb-2 ${
                isDarkMode ? 'text-white' : 'text-gray-900'
              }`}>
                Nenhuma pergunta encontrada
              </h3>
              <p className={isDarkMode ? 'text-gray-400' : 'text-gray-600'}>
                Tente ajustar os filtros ou pesquisar por outros termos
              </p>
            </div>
          )}
        </div>
      </section>

      {/* Contact Section */}
      <section className={`py-20 px-6 ${
        isDarkMode ? 'bg-gray-800' : 'bg-gradient-to-br from-blue-50 to-purple-50'
      }`}>
        <div className="max-w-4xl mx-auto text-center">
          <h2 className={`text-3xl md:text-4xl font-bold mb-6 ${
            isDarkMode ? 'text-white' : 'text-gray-900'
          }`}>
            Não Encontrou a Resposta?
          </h2>
          <p className={`text-xl mb-8 ${isDarkMode ? 'text-gray-400' : 'text-gray-600'}`}>
            Nossa equipa está pronta para ajudar. Entre em contacto connosco!
          </p>
          
          <div className="flex flex-col sm:flex-row gap-4 justify-center">
            <a
              href="tel:+244945574552"
              className="flex items-center justify-center space-x-2 px-8 py-4 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded-full font-semibold hover:shadow-2xl hover:shadow-blue-500/50 transition-all"
            >
              <Phone size={20} />
              <span>Ligar Agora</span>
            </a>
            <a
              href="mailto:geral@angonet.ao"
              className={`flex items-center justify-center space-x-2 px-8 py-4 border-2 rounded-full font-semibold transition-all ${
                isDarkMode
                  ? 'border-white/20 text-white hover:bg-white/5'
                  : 'border-gray-300 text-gray-700 hover:bg-white hover:border-blue-500'
              }`}
            >
              <Mail size={20} />
              <span>Enviar Email</span>
            </a>
          </div>
        </div>
      </section>

      <Footer isDarkMode={isDarkMode} />

      <style jsx>{`
        @keyframes float {
          0%, 100% {
            transform: translateY(0px);
          }
          50% {
            transform: translateY(-20px);
          }
        }
        .animate-float {
          animation: float 3s ease-in-out infinite;
        }
      `}</style>
    </div>
  );
}